import { createContext, useCallback, useContext, useMemo, useState } from "react";

/** Minimal job event fields for invoice UI and create payload (uids). */
export type JobEventForInvoice = {
  uid: string;
  startDate: string;
  endDate: string;
  status: string;
};

/** Chronological by startDate; invalid or missing dates sort last. */
export function sortJobEventsByStartDate<T extends { startDate: string }>(events: T[]): T[] {
  const time = (startDate: string) => {
    const t = new Date(startDate).getTime();
    return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
  };

  return [...events].sort((a, b) => time(a.startDate) - time(b.startDate));
}

export type JobEventsBoardContextValue = {
  isCreateInvoiceForMultipleVisitsOpen: boolean;
  setIsCreateInvoiceForMultipleVisitsOpen: (isOpen: boolean) => void;

  jobEvents: JobEventForInvoice[];
  setJobEvents: (jobEvents: JobEventForInvoice[]) => void;
  selectJobEvent: (jobEvent: JobEventForInvoice) => void;
  unSelectJobEvent: (uid: string) => void;
  isJobEventSelected: (uid: string) => boolean;

  isBulkWorkforceOpen: boolean;
  setIsBulkWorkforceOpen: (isOpen: boolean) => void;

  bulkWorkforceEvents: JobEventForInvoice[];
  setBulkWorkforceEvents: (jobEvents: JobEventForInvoice[]) => void;
  selectBulkWorkforceEvent: (jobEvent: JobEventForInvoice) => void;
  unSelectBulkWorkforceEvent: (uid: string) => void;
  isBulkWorkforceEventSelected: (uid: string) => boolean;
};

/** No default consumer value: without Provider, hooks must throw (avoids silent no-op setters). */
export const JobEventsBoardContext = createContext<JobEventsBoardContextValue | undefined>(undefined);

export const useJobEventsBoardContext = () => {
  const context = useContext(JobEventsBoardContext);

  if (context === undefined) {
    throw new Error("useJobEventsBoardContext must be used within a JobEventsBoardProvider");
  }

  return context;
};

export const JobEventsBoardProvider = ({ children }: { children: React.ReactNode }) => {
  const [isCreateInvoiceForMultipleVisitsOpen, setIsCreateInvoiceForMultipleVisitsOpen] = useState(false);
  const [jobEvents, setJobEvents] = useState<JobEventForInvoice[]>([]);

  const [isBulkWorkforceOpen, setIsBulkWorkforceOpen] = useState(false);
  const [bulkWorkforceEvents, setBulkWorkforceEvents] = useState<JobEventForInvoice[]>([]);

  const selectJobEvent = useCallback((jobEvent: JobEventForInvoice) => {
    setJobEvents((prev) => {
      if (prev.some((e) => e.uid === jobEvent.uid)) return prev;

      return sortJobEventsByStartDate([...prev, jobEvent]);
    });
  }, []);

  const unSelectJobEvent = useCallback((uid: string) => {
    setJobEvents((prev) => prev.filter((e) => e.uid !== uid));
  }, []);

  const isJobEventSelected = useCallback((uid: string) => jobEvents.some((e) => e.uid === uid), [jobEvents]);

  const selectBulkWorkforceEvent = useCallback((jobEvent: JobEventForInvoice) => {
    setBulkWorkforceEvents((prev) => {
      if (prev.some((e) => e.uid === jobEvent.uid)) return prev;

      return sortJobEventsByStartDate([...prev, jobEvent]);
    });
  }, []);

  const unSelectBulkWorkforceEvent = useCallback((uid: string) => {
    setBulkWorkforceEvents((prev) => prev.filter((e) => e.uid !== uid));
  }, []);

  const isBulkWorkforceEventSelected = useCallback((uid: string) => bulkWorkforceEvents.some((e) => e.uid === uid), [bulkWorkforceEvents]);

  const value = useMemo<JobEventsBoardContextValue>(
    () => ({
      isCreateInvoiceForMultipleVisitsOpen,
      setIsCreateInvoiceForMultipleVisitsOpen,

      jobEvents,
      setJobEvents,
      selectJobEvent,
      unSelectJobEvent,
      isJobEventSelected,

      isBulkWorkforceOpen,
      setIsBulkWorkforceOpen,

      bulkWorkforceEvents,
      setBulkWorkforceEvents,
      selectBulkWorkforceEvent,
      unSelectBulkWorkforceEvent,
      isBulkWorkforceEventSelected,
    }),
    [
      isCreateInvoiceForMultipleVisitsOpen,
      jobEvents,
      selectJobEvent,
      unSelectJobEvent,
      isJobEventSelected,
      isBulkWorkforceOpen,
      bulkWorkforceEvents,
      selectBulkWorkforceEvent,
      unSelectBulkWorkforceEvent,
      isBulkWorkforceEventSelected,
    ],
  );

  return <JobEventsBoardContext.Provider value={value}>{children}</JobEventsBoardContext.Provider>;
};

export type JobEventsBoardHook = JobEventsBoardContextValue;

export const useJobEventsBoard = (): JobEventsBoardHook => {
  const context = useContext(JobEventsBoardContext);

  if (context === undefined) {
    throw new Error("useJobEventsBoard must be used within a JobEventsBoardProvider");
  }

  return context;
};
